/**
 * Learning Path Statistics Page
 * Shows v1 analytics: anonymous/authenticated visits and element/media/link/quiz clicks only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Row, Spin, Statistic, Table } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import { getLearningPathStatistics } from '../../services/learningPathsService';
import styles from './LearningPaths.module.css';

/**
 * Renders visit and click statistics for a Learning Path.
 * @returns {JSX.Element} Statistics page
 */
const LearningPathStatistics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getLearningPathStatistics(id);
      setStats(result);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const columns = [
    { title: 'Element', dataIndex: 'elementName', key: 'elementName' },
    { title: 'Clicks', dataIndex: 'elementClicks', key: 'elementClicks' },
    { title: 'PDF', dataIndex: 'pdfOpens', key: 'pdfOpens' },
    { title: 'Audio', dataIndex: 'audioOpens', key: 'audioOpens' },
    { title: 'Video', dataIndex: 'videoOpens', key: 'videoOpens' },
    { title: 'Links', dataIndex: 'linkOpens', key: 'linkOpens' },
    { title: 'Quizzes', dataIndex: 'quizOpens', key: 'quizOpens' },
    { title: 'Surveys', dataIndex: 'surveyOpens', key: 'surveyOpens' },
    { title: 'Training Profiles', dataIndex: 'trainingProfileOpens', key: 'trainingProfileOpens' },
  ];

  return (
    <PagesWrapper>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Learning Path Statistics</h1>
          <p className={styles.pageDescription}>V1 analytics track visits and clicks only. Duration, starts, progress, and completion are intentionally not tracked.</p>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/learning-paths/${id}`)}>Back to Editor</Button>
      </div>

      {loading ? <Spin /> : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}><Card><Statistic title="Total Visits" value={stats?.totalVisits || 0} /></Card></Col>
            <Col xs={24} md={8}><Card><Statistic title="Unique Visitors" value={stats?.uniqueVisitors || 0} /></Card></Col>
            <Col xs={24} md={8}><Card><Statistic title="Element Clicks" value={stats?.totalElementClicks || 0} /></Card></Col>
          </Row>
          <Card title={stats?.learningPathName || 'Learning Path'}>
            <Table rowKey="elementId" dataSource={stats?.elementStatistics || []} columns={columns} pagination={false} />
          </Card>
          <Card title="Daily trend" style={{ marginTop: 16 }}>
            <Table
              rowKey="date"
              dataSource={stats?.trend || []}
              columns={[
                { title: 'Date', dataIndex: 'date', render: (date) => new Date(date).toLocaleDateString() },
                { title: 'Visits', dataIndex: 'visits' },
                { title: 'Clicks', dataIndex: 'clicks' },
              ]}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </>
      )}
    </PagesWrapper>
  );
};

export default LearningPathStatistics;
